<template>
	<div>
		<div id="traveller-map" class="traveller_map" ref="traveller_map"></div>
		<template v-for="(item, id) in this.overlays.click">
			<ClickOverlay
				:key="id"
				@close="onPopupClose"
				v-bind:popupid="id"
				:ref="id"
				@itemClicked="singleFeatureClickHandler"
				v-bind:featProps="item.feature"
				v-bind:map="comp.map"
			>
			</ClickOverlay>
		</template>
		<div
			v-show="this.overlays.selection != null"
			class="selection_overlay_div"
			ref="selectionDiv"
		>
			<svg height="60" width="60">
				<circle
					cx="30"
					cy="30"
					r="21"
					stroke="#b4365b"
					stroke-width="3"
					fill="none"
				/>
			</svg>
		</div>
		<div
			v-show="this.overlays.hover.feat != null"
			class="hover_overlay_div"
			ref="hoverDiv"
		>
			<MapTooltip v-bind:item="this.overlays.hover.feat"></MapTooltip>
		</div>
		<button v-if="debug" @click="DEBUG">debug!</button>

		<div id="map-actions">
			<div class="show-vehicles" @click="showVehicles = !showVehicles">
				<i class="nc-icon nc-bus-front-12" style="font-size: 22px"></i
				><input type="checkbox" v-model="showVehicles" />
			</div>
			<div @click="showStops = !showStops">
				<b-icon icon="flag" scale="1.3"></b-icon
				><input type="checkbox" v-model="showStops" />
			</div>
			<div @click="showDevices = !showDevices">
				<b-icon icon="display" scale="1.4"></b-icon
				><input type="checkbox" v-model="showDevices" />
			</div>
		</div>
		<div id="history-actions" v-if="vehicleHistory.active">
			<b-container class="containeri">
				<div id="play">
					<b-button
						size="sm"
						@click="vehicleHistory.autorun = vehicleHistory.autorun == false"
						v-b-tooltip
						:title="$t('map.vehicleHistory.play_button')"
					>
						<b-icon
							:icon="!vehicleHistory.autorun ? 'play' : 'pause-fill'"
							scale="1.4"
						></b-icon>
					</b-button>
				</div>
				<div id="skip">
					<b-button
						size="sm"
						@click="vehicleHistory.historyMomentIndex -= 2"
						v-b-tooltip
						:title="$t('map.vehicleHistory.rewind_button_bw')"
						><b-icon icon="chevron-double-left" scale="1"></b-icon
					></b-button>
				</div>
				<div>
					<b-form-input
						size="sm"
						id="history_range"
						v-model.number="vehicleHistory.historyMomentIndex"
						:max="vehicleHistory.historyMomentCount"
						min="0"
						type="range"
					></b-form-input>
				</div>
				<div id="skip">
					<b-button
						size="sm"
						@click="vehicleHistory.historyMomentIndex += 2"
						v-b-tooltip
						:title="$t('map.vehicleHistory.rewind_button_fw')"
						><b-icon icon="chevron-double-right" scale="1"></b-icon
					></b-button>
				</div>
				<div id="exit">
					<b-button
						size="sm"
						@click="deactivateVehicleHistoryOnMap(false)"
						v-b-tooltip
						:title="$t('map.vehicleHistory.close_button')"
						><b-icon icon="X" scale="1.4"></b-icon
					></b-button>
				</div>
				<div id="timestamp">{{ vehicleHistory.historyMomentTimeStamp }}</div>
			</b-container>
		</div>

		<div v-if="showSlideInClose" class="slide-in-close" @click="closeSlideIn">
			<b-icon icon="chevron-double-right" scale="1.2"></b-icon>
		</div>
	</div>
</template>

<script>
import ClickOverlay from '@/components/Map/ClickOverlay'
import MapTooltip from '@/components/Map/MapTooltip'
import moment from 'moment'
import mapstore from '@/store/map/mapstore'
import system from '@/store/system/system'

// import open layers
import View from 'ol/View'
import Map from 'ol/Map'
import TileLayer from 'ol/layer/Tile'
import Overlay from 'ol/Overlay'
import OSM from 'ol/source/OSM'
import { getBottomLeft, getTopRight } from 'ol/extent';

import 'ol/ol.css'

import olf from '@/components/Map/OpenLayersFunctions.js'
import utils from '@/plugins/TravellerUtils'


export default {
	name: 'TravellerMap',
	props: {
		vehicleLayerDisabled: { type: Boolean, default: false },
		stopLayerDisabled: { type: Boolean, default: false },
		routeStopsLayerDisabled: { type: Boolean, default: true },
		deviceLayerDisabled: { type: Boolean, default: false },
		featureListControlDisabled: { type: Boolean, default: false },
		centerPoint: { type: Array, default: null },
		zoomLevel: { type: Number, default: null },
		debug: { type: Boolean, default: false },
		showSlideInClose: { type: Boolean, default: false },
	},
	components: {
		ClickOverlay,
		MapTooltip,
	},
	data: function () {
		return {
			comp: {
				mapInitialized: false,
				map: null,
				view: null,
				interactions: {
					select: null,
				},
				layers: {
					base: null,
					route: null,
					gpsRoute: null,
					vehicle: null,
					stop: null,
					routeStop: null,
					device: null,
				},
				styleSet: null,
			},
			mapConfig: null,
			vehicleDeviationColors: null,
			overlays: {
				click: {},
				selection: null,
				selectionObject: null,
				hover: {
					feat: null,
					overlayObject: null,
					divElement: null,
				},
			},
			realtimeTimer: {
				vehicle: {
					timerId: null,
					timestamp: 0,
					active: true,
				},
				device: {
					timerId: null,
					timestamp: 0,
					active: true,
				},
			},
			showVehicles: true,
			showStops: true,
			showRouteStops: true,
			showDevices: true,
			vehicleHistory: {
				active: false,
				selectedDate: null,
				historyMomentIndex: 0,
				historyMomentCount: 0,
				historyMomentTimeStamp: '',
				autorun: false,
				historyData: null,
				currentHistoryMomentDetails: null,
				lastLoadedRouteId: null,
			},
		}
	},
	beforeCreate: function () {
		if (!this.$store.state.mapstore) {
			this.$store.registerModule('mapstore', mapstore)
		}
		if (!this.$store.state.system) {
			this.$store.registerModule('system', system)
		}
			var newFilter = {
				text: "",
			}
			this.$store.commit('mapstore/setVehicleFilter',newFilter);
			this.$emit("filterUpdated"); 
	},
	beforeDestroy: function () {
		this.realtimeTimer.vehicle.active = false
		clearTimeout(this.realtimeTimer.vehicle.timerId)
		clearTimeout(this.realtimeTimer.device.timerId)
	},
	mounted() {
		this.getMapConfiguration().then(
			() => {
				this.initMap()
			},
			() => {
				this.$travellerGetDataError()
			}
		)
		setTimeout(() => {
			location.reload();
		}, 4 * 60 * 60 * 1000);
	},
	watch: {
		'realtimeTimer.vehicle.timestamp': function () {
			if (!this.realtimeTimer.vehicle.active) return
			const delay =
				this.mapConfig.vehicleLayer.realtimeData.updateIntervalSeconds * 1000

			clearTimeout(this.realtimeTimer.vehicle.timerId)
			const timer = setTimeout(() => {
				clearTimeout(timer)
				this.refreshMapData('vehicle')
			}, delay)
			this.realtimeTimer.vehicle.timerId = timer
		},
		'realtimeTimer.device.timestamp': function () {
			if (!this.realtimeTimer.device.active) return

			const delay = this.mapConfig.deviceLayer.updateIntervalSeconds * 1000

			clearTimeout(this.realtimeTimer.device.timerId)
			this.realtimeTimer.device.timerId = setTimeout(
				function () {
					this.refreshMapData('device')
				}.bind(this),
				delay
			)
		},

		showVehicles() {
			this.comp.layers.vehicle.setVisible(this.showVehicles)
		},
		showStops() {
			this.comp.layers.stop.setVisible(this.showStops)
		},
		showRouteStops() {
			this.comp.layers.routeStops.setVisible(this.showRouteStops)
		},
		showDevices() {
			this.comp.layers.device.setVisible(this.showDevices)
		},
		'vehicleHistory.currentHistoryMomentDetails.route': function (newValue, oldValue) {
			if (!this.vehicleHistory.active) return
			if (newValue === oldValue) return
			if (!newValue) {
				this.hideScheduledRouteOnMap()
				this.hideGpsRouteOnMap()
				this.showCustomRouteOnMap(
					olf.createRouteFromHistory(this.vehicleHistory.historyData)
				) 
				return;
			}

			this.showScheduledRouteOutlineOnMap(newValue)
			this.vehicleHistory.lastLoadedRouteId = newValue
			const currentTripId = this.vehicleHistory.currentHistoryMomentDetails.tripId
			this.showGpsRouteForHistoricalRoute(newValue, currentTripId)
		},
		'vehicleHistory.historyMomentIndex': function (newVal) {
			if (!this.vehicleHistory.active) return
			this.showVehiclePointInHistory(newVal)
			this.$store.commit(
				'mapstore/setActiveVehicleHistoryDetails',
				this.vehicleHistory.historyData[newVal]
			)
			this.currentHistoryMomentDetails = this.vehicleHistory.historyData[newVal]
		},
		'vehicleHistory.autorun': function (newVal) {
			if (newVal) {
				this.vehicleHistoryTimerTick()
			}
		},

		updateCurrentVehicleHistory: function (newDetails) { 
			this.$store.commit('mapstore/setActiveVehicleHistoryDetails', newDetails)
		},
		vehiclesForMap: function (newData) {

			const layer = this.comp.layers.vehicle
			const source = layer.getSource()
			//const $vm = this;
			// Check if we had vehicle selected and we need to move selection to point new position of
			// vehicle
			let identifier = null
			if (this.overlays.selectionObject != null) {
				try {
					if (this.overlays.selectionObject.vehicleLabel != undefined)
						identifier = this.overlays.selectionObject.vehicleLabel
					else identifier = this.overlays.selectionObject.values_.vehicleLabel
				} catch (err) {
					/*  */
				}
			}
			const features = source.getFormat().readFeatures(newData)
			if (this.vehicleHistory.active == false) {
				this.comp.layers.vehicle.getSource().clear()
				this.comp.layers.vehicle.getSource().addFeatures(features)
			}
			const relevants = this.comp.layers.vehicle.getSource().getFeatures()
			relevants.forEach((feat) => {
                feat.setStyle(olf.getStyleForVehicleFeature(feat, this.comp.styleSet))
				if (identifier != null && feat.getKeys().includes('vehicleLabel')) {
					if (identifier == feat.getProperties().vehicleLabel) {
						this.overlays.selection.setPosition(
							feat.getGeometry().getFirstCoordinate()
						)
					}
				}
			})

			this.realtimeTimer.vehicle.timestamp = new Date().getTime()
		},
	},
	computed: {
		currentVehicleHistory: function () {
			if (!this.vehicleHistory.historyData) return null
			return this.vehicleHistory.historyData[
				this.vehicleHistory.historyMomentIndex
			]
		},
		currentHistoryMomentDetails: {
			get() {
				if (!this.vehicleHistory.historyData) return null
				return this.vehicleHistory.currentHistoryMomentDetails
			},
			set(details) {
				this.vehicleHistory.currentHistoryMomentDetails = details
			},
		},
		vehiclesForMap() {
			return this.$store.state.mapstore.vehiclesForMap
		},
	},
	methods: {
		initMap: function () {
			const baselayer = this.createTravellerBaseLayer()
			this.comp.layers.base = baselayer
			this.showRouteStops = false;
			const selectLayers = new Array()
			const map = new Map({
				target: this.$refs['traveller_map'],
				layers: [baselayer],
				view: this.createMapView(),
			})

			this.comp.map = map

			this.comp.styleSet = olf.initStyles(this.vehicleDeviationColors, this.mapConfig, this.comp.view);
            map.on('moveend', () => {
                let extent = this.comp.view.calculateExtent();
                this.$store.dispatch('mapstore/updateVehiclesForMap', 
                    {
                        minX: getBottomLeft(extent)[0],
                        minY: getBottomLeft(extent)[1],
                        maxX: getTopRight(extent)[0],
                        maxY: getTopRight(extent)[1]
                    }
                );
            });

			/* Next, different data layers are made depending on whether they are enabled or not */

			/*
        Routes layer (sheduled routes)
        The routes layer has a static style to display routes (already given here)
      */
			const routes = olf.createRouteLayer(this.comp.styleSet.routes.line)
			this.comp.layers.route = routes
			map.addLayer(routes, 'scheduledRouteLayer')

			/*
        GPS routes layer
        The route layer has a ststic style to diplay routes (already given here)
      */
			const gpsLayer = olf.createRouteLayer(this.comp.styleSet.routes.gpsRoute)
			this.comp.layers.gpsRoute = gpsLayer
			map.addLayer(gpsLayer, 'gpsRouteLayer')

			// Route stops layer 
			const routeStops = olf.createRouteStopLayer();
			this.comp.layers.routeStops = routeStops;
			selectLayers.push(routeStops);
			map.addLayer(routeStops, 'routeStopsLayer');
			routeStops.setStyle(this.routeStopLayerStyleFunction);

			// Stop layer
			const stops = olf.createStopLayer()
			this.comp.layers.stop = stops
			selectLayers.push(stops)
			stops.getSource().setLoader(this.travellerStopDataLoader)
			map.addLayer(stops, 'stopsLayer')
			stops.setStyle(this.stopLayerStyleFunction)

			// Device layer 
			const devs = olf.createDeviceLayer()
			this.comp.layers.device = devs
			selectLayers.push(devs)
			devs.getSource().setLoader(this.travellerDevicesDataLoader)
			map.addLayer(devs, 'deviceLayer')

			// Vehicle layer 
			const vehicles = olf.createVehicleLayer()
			this.comp.layers.vehicle = vehicles
			selectLayers.push(vehicles)

			// Tested by requesting vehicles from traveller to see if they actually exist
			// If not, then datasource is not added to vehicle layer so that the map doesn't ask for further vehicle data from Traveller

			const $vm = this
			this.$store
				.dispatch(
					'mapstore/getVehiclesForTable',
					this.realtimeUpdateIntervalSec
				)
				.then(
					(data) => {
						if (data.length > 0) {
							$vm.comp.layers.vehicle
								.getSource()
								.setLoader($vm.travellerVehicleRealtimeDataLoader)
							$vm.refreshMapData('vehicle')
						}
					},
					() => {
						/*Ei tehdä mitään*/
					}
				)

			map.addLayer(vehicles, 'vehiclesLayer')

			// tehdään valinnan interaction
			const selectInter = olf.createSelectInteraction(
				selectLayers,
				this.onFeatureClick
			)
			this.comp.interactions.select = selectInter
			map.addInteraction(selectInter)

			this.createFeatureHover()
		}, // end of initMap()
		createFeatureHover: function () {
			const map = this.comp.map
			const overldiv = this.$refs.hoverDiv
			this.overlays.hover.divElement = overldiv
			const popup = new Overlay({
				element: overldiv,
				positioning: 'bottom-center',
				offset: [0, -20],
			})
			this.overlays.hover.overlayObject = popup
			popup.setElement(overldiv)
			map.addOverlay(popup)

			map.on('pointermove', (e) => {
				if (this.overlays.hover.feat !== null) {
					this.overlays.hover.feat = null
				}

				map.forEachFeatureAtPixel(e.pixel, (f) => {
					if(f.values_.pointerEvents == 'none') {
						return true;
					}
					this.overlays.hover.feat = f
					const coord = f.getGeometry().getFirstCoordinate()
					this.overlays.hover.overlayObject.setPosition(coord)
					return true
				})
			})
		},
		createTravellerBaseLayer: function () {
			const layer = new TileLayer({
				source: new OSM({ url: this.mapConfig.baselayers[0].url }),
			})
			// Nostetaan lippu että kartta on alustettu ja valmis ottamaan vastaan
			// komentoja. huom. kartan materiaalit eivät ole kuitenkaan täysin latautuneet
			layer.getSource().once('tileloadstart', () => {
				this.comp.mapInitialized = true
			})
			return layer
		},
		updateData() {
			this.$store.dispatch('mapstore/getVehiclesForTable', this.realtimeUpdateIntervalSec) 
		},
		createOSMBaseLayer: function () {
			return new TileLayer({
				source: new OSM(),
			})
		},
		createMapView: function () {
			this.comp.view = new View({
				zoom: this.mapConfig.viewconfig.zoom,
				center: this.mapConfig.viewconfig.center,
				constrainResolution: true,
			})
			return this.comp.view
		},
		getMapConfiguration: function () {
			return new Promise((resolve, reject) => {
				const devColors = this.$store.dispatch(
					'mapstore/system/getDeviationColors'
				)
				const mapConfig = this.$store.dispatch('mapstore/getMapConfig')
				return Promise.all([devColors, mapConfig]).then(
					(data) => {
						this.mapConfig = data[1]
						this.vehicleDeviationColors = data[0]
						resolve(data)
					},
					() => {
						this.$travellerGetDataError()
						reject()
					}
				)
			})
		},
		stopLayerStyleFunction: function (feat, resolution) {
			olf.getStyleForStopFeature(feat, resolution, this.comp.styleSet)

		},
		routeStopLayerStyleFunction: function (feat, resolution) {
			olf.getStyleForRouteStopFeature(feat, resolution, this.comp.styleSet)
		},
		travellerStopDataLoader: function (/*extent, resolution, projection*/) {
			/* Vektoridata travellerista pitää tulla storen kautta jotta auth tokenit menee oikein.
            Samoin saadaan viimeisin data storeen talteen filtteröitäväksi myöhemmin.
            Openlayersin vector source loader function parametrit ei ole käytössä eikä toteutettu
            koska datamäärät on niin pieniä ettei haettavaa matskua tarvii niillä rajata.
            palautetaan aina kaikki.*/
			const layer = this.comp.layers.stop
			const vectorSource = layer.getSource()

			this.$store.dispatch('mapstore/getStopsForMap').then(
				(data) => {
					const features = vectorSource.getFormat().readFeatures(data)
					vectorSource.addFeatures(features)
				},
				() => {
					this.$travellerGetDataError()
				}
			)
		},

		vehicleFilterUpdated: function () {
			try {
				const layer = this.comp.layers.vehicle
				const source = layer.getSource()
				source.clear()
				this.travellerVehicleRealtimeDataLoader()
			} catch (err) {
				// do nothing
			}
		},
		travellerVehicleRealtimeDataLoader:
			function (/*extent, resolution, projection*/) {
                let extent = this.comp.view.calculateExtent();

				this.$store.dispatch(
					'mapstore/getVehiclesForMap',
					{
                        expireTime: this.mapConfig.vehicleLayer.realtimeData.updateIntervalSeconds,
                        minX: getBottomLeft(extent)[0],
                        minY: getBottomLeft(extent)[1],
                        maxX: getTopRight(extent)[0],
                        maxY: getTopRight(extent)[1]
                    }
				);
                
			},
		travellerDevicesDataLoader: function (/*extent, resolution, projection*/) {
			const layer = this.comp.layers.device
			const source = layer.getSource()
			this.$store
				.dispatch(
					'mapstore/getDevicesForMap',
					this.mapConfig.deviceLayer.updateIntervalSeconds
				)
				.then((data) => {
					const features = source.getFormat().readFeatures(data)
					features.forEach((feat) => {
						feat.setStyle(
							olf.getStyleForDeviceFeature(feat, this.comp.styleSet)
						)
					})
					source.clear()
					source.addFeatures(features)
				})
				.catch(() => {
					this.$travellerGetDataError()
				})
				.finally(() => {
					this.realtimeTimer.device.timestamp = new Date().getTime()
				})
		},
		onVehicleClick(event) {
			if (event.target.getFeatures().getLength() > 0) {
				const feat = event.target.getFeatures().getArray()
				feat.forEach((f) => {
					olf.toggleSelectionStyle(f, this.comp.styleSet)
				})
				let popupdata = {}
				popupdata.feature = feat[0]
				popupdata.id = utils.makeID('vehicle')
				this.$set(this.overlays.click, popupdata.id, popupdata)
			}

			if (event.deselected.length > 0) {
				const unselected = event.deselected
				unselected.forEach((f) => {
					olf.toggleSelectionStyle(f, this.comp.styleSet)
				})
			}
		},
		onFeatureClick(event) {
			// tee deselectointi
			this.deselectFeatures(event.deselected)
			if (event.selected.length == 0) {
				// ei osunut mihinkään.
				// klikattiin kartalla johonkin ja jos overlay-valikoita on niin sulje ne nyt
				var newFilter = {
					text: "",
				}
				this.$store.commit('mapstore/setVehicleFilter',newFilter);
				this.updateData();
				this.$emit("filterUpdated"); 
				const $vm = this
				const keys = Object.keys(this.overlays.click)
				keys.forEach((k) => {
					$vm.$delete($vm.overlays.click, k)
				})
				return
			}

			// tarkastele mihin kaikkiin osuttiin. Kerää ne listaan
			const osumat = new Array()
			event.selected.forEach((sel) => {
				const sisaFeaturet = sel.get('features')
				if (sisaFeaturet) {
					sisaFeaturet.forEach((e) => {
						osumat.push(e)
					})
				} else {
					osumat.push(sel)
				}
			})
			if (osumat.length > 1) {
				// osuttiin useaan. näytä valikko
				let popupdata = {}
				popupdata.feature = osumat
				popupdata.id = utils.makeID('click')
				this.$set(this.overlays.click, popupdata.id, popupdata)
			} else {
				// Oli vain yksi feature. selectoi se nyt
				this.singleFeatureClickHandler(osumat[0])
			}
		},
		deselectFeatures(feats) {
			this.hideSelectionOverlay()
			if (Array.isArray(feats)) {
				/*  tyjää toteuta joskus */
			} else {
				feats = this.comp.interactions.select.getFeatures()
			}
		},
		singleFeatureClickHandler(feat) {
			// Jos kyse oli Featuresta niin ID löytyy sieltä.
			let geom = null
			if (typeof feat.getGeometry === 'function') {
				geom = feat.getGeometry()
				if (feat.getKeys().includes('vehicleId')) {
					// ajoneuvo Feature
					const vehicleData = feat.get('vehicleData')
					const vehiclelabel = vehicleData.vehicleLabel
					// Hide other buses on map
					const newFilter = {
						text: vehiclelabel,
					}
					this.$store.commit('mapstore/storeFilter');
					this.$store.commit('mapstore/setVehicleFilter',newFilter);
					this.updateData();
					this.$emit("filterUpdated");
					this.$emit('vehicleFeatureClicked', vehicleData.vehicleId)
				} else if (feat.getKeys().includes('deviceId')) {
					const deviceId = feat.getProperties().deviceId
					this.$emit('deviceFeatureClicked', deviceId)
				} else if (feat.getKeys().includes('stopData')) {
					// stop Feature
					this.$emit('stopFeatureClicked', feat.get('tunnus'))
				}
			} else {
				// Tässä on tapaus jossa klikki tulee Overlayn kautta valintalistasta
				let source = null
				let f = null
				let ufeat = null
				if (feat.tunnus) {
					source = this.comp.layers.stop.getSource()
					f = source.getFeatures()
					ufeat = f.find((i) => {
						const props = i.getProperties()
						return props.tunnus == feat.tunnus
					})
					geom = ufeat.getGeometry()
					this.$emit('stopFeatureClicked', feat.tunnus)
				} else if (feat.deviceId != undefined || feat.device != undefined) {
					source = this.comp.layers.device.getSource()
					f = source.getFeatures()
					let devId = null
					ufeat = f.find((i) => {
						const props = i.getProperties()
						try {
							if (feat.device != undefined && feat.device != null) {
								devId = feat.device.deviceId
								return feat.device.deviceId == props.deviceId
							} else {
								devId = feat.deviceId
								return feat.deviceId == props.deviceId
							}
						} catch (err) {
							return false
						}
					})
					geom = ufeat.getGeometry()
					this.$emit('deviceFeatureClicked', devId)
				} else if (feat.vehicleId) {
					source = this.comp.layers.vehicle.getSource()
					f = source.getFeatures()
					ufeat = f.find((i) => {
						const props = i.getProperties()
						return props.vehicleData.vehicleId == feat.vehicleId
					})
					geom = ufeat.getGeometry()
					const vehiclelabel = feat.vehicleLabel
					const newFilter = {
						text: vehiclelabel,
					}
					this.$store.commit('mapstore/setVehicleFilter',newFilter);
					this.updateData();
					this.$emit("filterUpdated");
					this.$emit('vehicleFeatureClicked', feat.vehicleId)
				}
			}
			this.showSelectionOverlay(geom.getFirstCoordinate(), feat)
		},
		panToPosition(point) {
			this.comp.view.animate({
				center: point,
				zoom: 17,
				duration: 1500,
			})
		},
		hideOtherVehicles(vehicleLabel) {
			const newFilter = {
				text: vehicleLabel,
			}
			this.$store.commit('mapstore/storeFilter');
			this.$store.commit('mapstore/setVehicleFilter',newFilter);
			this.updateData();
			this.$emit("filterUpdated");
		},
		addRouteFeatures(source, features, label, style) {
			features.forEach((feature) => {
				const properties = feature.getProperties()
				properties.label = label
				feature.setProperties(properties)
				if (style !== null && style !== undefined) feature.setStyle(style)
			})
			source.addFeatures(features)
		},
		showRouteOnMap: function (routeId) {
			this.showRouteStopsOnMap(routeId)
			const layer = this.comp.layers.route
			const source = layer.getSource()
			const view = this.comp.view
			this.$store
				.dispatch('mapstore/getRouteSpatialData', routeId)
				.then((data) => {
					const features = source.getFormat().readFeatures(data)
					this.addRouteFeatures(source, features, 'scheduledRoute', null)
					const first = features[0]
					const extent = first.getGeometry().getExtent() 
					view.fit(extent, { duration: 1000 })
				})
				.catch(() => { 
					this.$notify({
						title: this.$t("alert.route_display_error.error"),
						message: this.$t("alert.route_display_error.message"),
						type: 'danger',
						timeout: 2000,
					})
				})
		},
		showLineRoutesOnMap: function (routeId) {
			this.hideRoutesOnMap();
			const layer = this.comp.layers.route
			const source = layer.getSource()
			const view = this.comp.view
			this.$store
				.dispatch('mapstore/getRouteSpatialData', routeId)
				.then((data) => {
					const features = source.getFormat().readFeatures(data)
					this.addRouteFeatures(source, features, 'scheduledRoute', null)
					const first = features[0]
					const extent = first.getGeometry().getExtent()
					view.fit(extent, { duration: 500 })  
				})
				.catch(() => {
					this.$notify({
						title: this.$t("alert.route_display_error.error"),
						message: this.$t("alert.route_display_error.message"),
						type: 'danger',
						timeout: 2000,
					})
				})
		},

		showRouteStopsOnMap: function (routeId) { 
			const routeStopLayer = this.comp.layers.routeStops;
			const routeStopLayerSource = routeStopLayer.getSource();
			const stopLayer = this.comp.layers.stop;
			const stopLayerSource = stopLayer.getSource();
			this.showRouteStops = true;
			
			this.$store 
				.dispatch('timetable/getRouteStops', routeId)
				.then((data) => {
					const routeStops = data.data.routestops || [];
					const routeStopsById = {};
					routeStops.forEach(stop => {
						routeStopsById[stop.tunnus] = stop;
					});
					
					routeStopLayerSource.clear()
					stopLayerSource.forEachFeature((stopFeature) => {
						const tunnus = stopFeature.get('tunnus');
						if(routeStopsById[tunnus] != null) {
							const feature = olf.createRouteStopFeature(stopFeature.values_.geometry.flatCoordinates, stopFeature.values_.tunnus)
							routeStopLayerSource.addFeature(feature)
						}
					})
					})

				.catch(() => {
					this.$notify({
						title: this.$t("alert.route_stops_display_error.error"),
						message: this.$t("alert.route_stops_display_error.message"),
						type: 'danger',
						timeout: 2000,
					})
				})
		},

		showScheduledRouteOutlineOnMap: function (routeId) {
			const layer = this.comp.layers.route
			const source = layer.getSource()
			const timestamp = this.vehicleHistory.historyData[0].timestamp
			const resultTime = moment(timestamp).format('YYYY-MM-DDTHH:mm:ssZZ');
			const params = {
				routeId: routeId,
				date: resultTime
			};
			this.$store
				.dispatch('mapstore/getHistoricRouteSpatialData', params)
				.then((data) => {
					const features = source.getFormat().readFeatures(data)
					const styles = this.comp.styleSet.routes.scheduledRouteOutline
					this.hideScheduledRouteOnMap()
					this.addRouteFeatures(source, features, 'scheduledRoute', styles)
				})
				.catch(() => {
					this.$notify({
						title: this.$t("alert.route_display_error.error"),
						message: this.$t("alert.route_display_error.message"),
						type: 'danger',
						timeout: 2000,
					})
				})
		},
		showCustomRouteOnMap: function (routeFeature, animate = false) {
			const source = this.comp.layers.gpsRoute.getSource()
			this.addRouteFeatures(source, [routeFeature], 'gpsRoute', null)
			if (animate) {
				const view = this.comp.view
				const extent = routeFeature.getGeometry().getExtent()
				view.fit(extent, { duration: 500 })
			}
		},
		showGpsRouteForHistoricalRoute: function(routeId, tripId) {
			this.hideGpsRouteOnMap()
			const firstIndex = this.vehicleHistory.historyData.findIndex(
				point => point.route === routeId && point.tripId === tripId
			);
			const lastIndex = this.vehicleHistory.historyData.findLastIndex(
				point => point.route === routeId && point.tripId === tripId
			);
			let routeGpsData = firstIndex !== -1 && lastIndex !== -1
				? this.vehicleHistory.historyData.slice(firstIndex, lastIndex + 1)
				: this.vehicleHistory.historyData.filter(point => point.route === routeId && point.tripId === tripId);

			if (routeGpsData.length === 0) {
				return;
			}
			const routeFeature = olf.createRouteFromHistory(routeGpsData)
			this.showCustomRouteOnMap(routeFeature)
		},
		hideRoutesOnMap: function () {
			const scheduledRoutesLayer = this.comp.layers.route
			scheduledRoutesLayer.getSource().clear()
			this.showRouteStops = false;
			const gpsRoutesLayer = this.comp.layers.gpsRoute
			gpsRoutesLayer.getSource().clear()
		},
		hideScheduledRouteOnMap: function () {
			const scheduledRoutesLayer = this.comp.layers.route
			// const featuresToRemove = scheduledRoutesLayer
			//   .getSource()
			//   .getFeatures()
			//   .filter((feature) => feature.get('label') === 'scheduledRoute');
			// featuresToRemove.forEach((feature) => scheduledRoutesLayer.getSource().removeFeature(feature));
			scheduledRoutesLayer.getSource().clear()
		},
		hideGpsRouteOnMap: function () {
			const gpsRoutesLayer = this.comp.layers.gpsRoute
			gpsRoutesLayer.getSource().clear()
		},
		onPopupClose: function (popupId) {
			this.$delete(this.overlays.click, popupId)
		},
		clearFilter: function () {
			this.updateData();
			var newFilter = {
				text: "",
			}
			this.$store.commit('mapstore/setVehicleFilter', newFilter);
			this.$emit("filterUpdated"); 
			this.refreshMapData('device')
			this.refreshMapData('vehicle')
		},
		restoreFilterOnSlideInClose() {
			this.updateData();
			this.$store.commit('mapstore/restoreFilterAndClearStored')
			this.$emit("filterUpdated"); 
			this.refreshMapData('device')
			this.refreshMapData('vehicle')
		},
		refreshMapData: function (layer) {
			let source = null
			if (layer == 'stop') {
				source = this.comp.layers.stop.getSource()
			}
			if (layer == 'routeStop') {
				source = this.comp.layers.routeStop.getSource()
			}
			if (layer == 'device') {
				this.travellerDevicesDataLoader()
				return
			}
			if (layer == 'vehicle') {
				this.travellerVehicleRealtimeDataLoader()
				return
			}

			if (source.getState() != 'LOADING') {
				source.refresh()
			}
		},
		showSelectionOverlay: function (coordinates, feat) {
			const overldiv = this.$refs.selectionDiv
			const popup = new Overlay({
				element: overldiv,
				positioning: 'center-center',
				stopEvent: false,
			})
			this.overlays.selection = popup
			if (feat != undefined && feat != null)
				this.overlays.selectionObject = JSON.parse(JSON.stringify(feat))
			popup.setElement(overldiv)
			popup.setPosition(coordinates)
			this.comp.map.addOverlay(popup)
		},
		hideSelectionOverlay: function () {
			const ov = this.overlays.selection
			if (ov != null && ov != undefined) {
				this.comp.map.removeOverlay(ov)
			}
			this.overlays.selection = null
			this.overlays.selectionObject = null
		},
		// This function is not really used.
		getVehicleHistoryForComputer: function (/*computerLabel, start, end*/) {
			const computerLabel = '644'
			const start = '2021-09-28T00:00:00+0300'
			const end = '2021-09-28T23:59:59+0300'
			this.$store
				.dispatch('mapstore/getVehicleHistory', [computerLabel, start, end])
				.then((data) => {
					this.vehicleHistory.historyData = data
					this.vehicleHistory.historyMomentCount = data.length - 1
					this.activateVehicleHistory()
				})
		},
		activateVehicleHistory: function (selectedDate) { 
			this.vehicleHistory.selectedDate = selectedDate
			this.vehicleHistory.historyData =
				this.$store.state.mapstore.vehicleHistory.historyData
			if (
				this.vehicleHistory.historyData == null ||
				this.vehicleHistory.historyData.length == 0
			)
				return 
			this.vehicleHistory.historyMomentCount =
				this.vehicleHistory.historyData.length - 1
			this.vehicleHistory.active = true
			this.vehicleHistory.historyMomentIndex = 0
			this.currentHistoryMomentDetails =
				this.$store.state.mapstore.vehicleHistory.historyData[0]
			this.showStops = true
			this.showRouteStops = false
			this.showDevices = false
			this.realtimeTimer.vehicle.active = false
			this.realtimeTimer.device.active = false
			// clear vehicle layer
			if (this.realtimeTimer.vehicle.timerId != null) {
				clearTimeout(this.realtimeTimer.vehicle.timerId)
			}
			const layer = this.comp.layers.vehicle
			const source = layer.getSource()
			source.clear()
			this.hideRoutesOnMap()
			this.hideSelectionOverlay()
			this.showCustomRouteOnMap(
				olf.createRouteFromHistory(this.vehicleHistory.historyData),
				true
			) 
			this.showVehiclePointInHistory(0)
		},

		deactivateVehicleHistory: function () {
			this.clearFilter();
		},
		deactivateVehicleHistoryOnMap: function (isClosingPanel = false) {
			this.vehicleHistory.active = false
			this.vehicleHistory.historyData = null
			this.vehicleHistory.autorun = false
			this.vehicleHistory.historyMomentIndex = 0
			this.vehicleHistory.historyMomentCount = 0
			this.currentHistoryMomentDetails = null
			this.showRouteStops = false
			this.realtimeTimer.vehicle.active = true
			this.realtimeTimer.device.active = true
			this.hideRoutesOnMap()
			const layer = this.comp.layers.vehicle
			const source = layer.getSource()
			source.clear()
			if(!isClosingPanel){
				this.clearFilter();
				this.showStops = true
				this.showDevices = true
			}
		},
		showVehiclePointInHistory: function (index) {
			if (index < 0) index = 0
			if (index > this.vehicleHistory.historyData.length - 1)
				index = this.vehicleHistory.historyData.length - 1

			const data = this.vehicleHistory.historyData
			const moment = data[index]
			this.vehicleHistory.historyMomentTimeStamp = olf.historyTimestampFormat(
				moment.timestamp
			)
			this.drawHistoricVehicleOnMap(moment)
		},
		drawHistoricVehicleOnMap: function (vehicleData) { 
			const feat = olf.createHistoricVehicleFeature(vehicleData)
			feat.setStyle(olf.getStyleForVehicleFeature(feat, this.comp.styleSet)) 
			const layer = this.comp.layers.vehicle
			const source = layer.getSource()
			source.clear()
			source.addFeatures([feat])
		},
		vehicleHistoryTimerTick: function () {
			if (!this.vehicleHistory.autorun) return

			if (
				this.vehicleHistory.historyMomentIndex <
				this.vehicleHistory.historyMomentCount
			) {
				this.vehicleHistory.historyMomentIndex =
					this.vehicleHistory.historyMomentIndex + 2
				setTimeout(() => {
					this.vehicleHistoryTimerTick()
				}, 500)
			} else {
				this.vehicleHistory.autorun = false
			}
		},
		DEBUG: function () {
			//debugger; 
		},
		closeSlideIn() {
			this.restoreFilterOnSlideInClose()
			this.$emit('closeSlideIn')
			this.deactivateVehicleHistoryOnMap(true)
		},
	},
}
</script>
